/**
 * Toast utilities with UX best practices
 *
 * Provides async toast patterns that ensure minimum display times
 * so users can read feedback even when API calls are fast.
 */
import { toast } from 'sonner';

/** Minimum time a loading toast should be visible (ms) */
const MIN_LOADING_DURATION = 1500;

/** Small delay between dismissing loading and showing success (ms) */
const TRANSITION_DELAY = 150;

interface ToastWithProgressOptions<T> {
  /** Message shown while loading */
  loading: string;
  /** Message shown on success - can be string or function that receives the result */
  success: string | ((result: T) => string);
  /** Message shown on error (optional, defaults to generic) */
  error?: string;
}

/**
 * Execute an async operation with loading → success/error toast progression.
 * Ensures the loading toast is visible for at least MIN_LOADING_DURATION
 * so users can read it even when the API is fast.
 *
 * @example
 * ```ts
 * // Static success message
 * await toastWithProgress(
 *   () => updateUser({ name: 'John' }).unwrap(),
 *   {
 *     loading: 'Updating user...',
 *     success: 'User updated',
 *   }
 * );
 *
 * // Dynamic success message based on result
 * await toastWithProgress(
 *   () => assignTasks({ ids }).unwrap(),
 *   {
 *     loading: 'Assigning tasks...',
 *     success: (result) => `${result.count} tasks assigned`,
 *   }
 * );
 * ```
 */
export async function toastWithProgress<T>(
  operation: () => Promise<T>,
  options: ToastWithProgressOptions<T>,
): Promise<T> {
  const startTime = Date.now();
  const loadingToastId = toast.loading(options.loading);

  try {
    const result = await operation();

    // Ensure minimum display time for loading toast
    const elapsed = Date.now() - startTime;
    const remainingTime = Math.max(0, MIN_LOADING_DURATION - elapsed);

    await sleep(remainingTime);
    toast.dismiss(loadingToastId);

    // Small delay before success toast for smooth transition
    await sleep(TRANSITION_DELAY);

    // Support both static string and dynamic function for success message
    const successMessage =
      typeof options.success === 'function'
        ? options.success(result)
        : options.success;
    toast.success(successMessage);

    return result;
  } catch (error) {
    toast.dismiss(loadingToastId);
    await sleep(TRANSITION_DELAY);
    toast.error(options.error || 'Something went wrong');
    throw error;
  }
}

/**
 * Promise-based sleep utility
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
